import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { red } from '@mui/material/colors';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import toString from 'lodash/toString';
import { ChangeEvent, Fragment, ReactNode } from 'react';
import { Controller, FieldValues } from 'react-hook-form';

import { SelectProps } from './types/SelectProps';

export const Select = <TFieldValues extends FieldValues = FieldValues>({
  control,
  disabled,
  errorTestId,
  inputTestId,
  label,
  name,
  onChange,
  options,
  rules,
}: SelectProps<TFieldValues>): ReactNode => (
  <Controller
    control={control}
    name={name}
    render={({ field, fieldState: { error } }) => {
      const isValueInOptions = options.some(
        (opt) => String(opt.value) === String(field.value),
      );
      // Fall back to '' when the current value is not in the available options
      // to prevent MUI from rendering an out-of-range select value
      const value = disabled || !isValueInOptions ? '' : field.value;

      const handleOnChange = (event: ChangeEvent<HTMLInputElement>): void => {
        field.onChange(event);

        if (onChange) {
          onChange(event.target.value);
        }
      };

      return (
        <Fragment>
          <TextField
            {...field}
            disabled={disabled}
            error={!disabled && Boolean(error)}
            label={label}
            slotProps={{
              select: { IconComponent: ExpandMoreRoundedIcon },
              htmlInput: { 'data-testid': inputTestId },
            }}
            value={value}
            fullWidth
            select
            onChange={handleOnChange}
          >
            <MenuItem value="" disabled>
              {label}
            </MenuItem>

            {options.map(
              ({ name: optionName, value: optionValue }): ReactNode => (
                <MenuItem key={String(optionValue)} value={optionValue}>
                  {optionName}
                </MenuItem>
              ),
            )}
          </TextField>

          {!disabled && error && (
            <span
              data-testid={errorTestId}
              style={{ fontSize: '12px', color: red[700] }}
            >
              {toString(error.message)}
            </span>
          )}
        </Fragment>
      );
    }}
    rules={rules}
  />
);
